import * as THREE from "three";

export class PBRMaterialManager {
    constructor() {
        this.materials = new Map();
        this.hairElements = new Map();
        this.hairDuplicates = new Map();
        this.textureLoader = new THREE.TextureLoader();
        this.setupTransparencyControls();

        // Hair color mapping for different character models by model ID
        this.modelHairColors = {
            // Default dark hair
            default: new THREE.Color(0x1a1a1a),

            //"44f87c0b-ca22-4225-9fea-8d823e6a0160": new THREE.Color(0xb0b0b0), // Warren Buffett - Gray
            "8944783c-2e2b-4796-b92e-21dacca776bc": new THREE.Color(0xb0b0b0), // Da Vinci - Gray
            "3542f19e-4e07-4828-8f9d-f330e4d6d660": new THREE.Color(0xb0b0b0), //Plato - Gray
            //"897eb00c-4f16-4f73-81d5-d1c91fad53f2": new THREE.Color(0xb0b0b0), //Charles Darwin - Gray
            "4d7d2e17-b818-4144-b78b-629c688a71ac": new THREE.Color(0x807c7b), //Karl Marx - Gray
            // "992e9007-b4f8-4711-80c2-884daf3d9960": new THREE.Color(0x4d2b1d), // Van Gogh - Red/brown
            //"278dddd6-b4a8-4303-a41d-be6219dbc907": new THREE.Color(0xa8a4a3), // Albert Einstein - Gray
            // "5a16ff56-6e03-414e-a89d-cda9d83fae60": new THREE.Color(0x5f5f5f), // Henry Ford - Gray
            // "b20dc26f-8924-4aee-9ef5-9b98c0367a34": new THREE.Color(0xb0b0b0), // Ernest Hemingway - Gray
        };

        // Beard color mapping for different character models by model ID
        this.modelBeardColors = {
            // Default beard color (slightly darker than hair)
            default: new THREE.Color(0x0f0f0f),

            // "44f87c0b-ca22-4225-9fea-8d823e6a0160": new THREE.Color(0xa0a0a0), // Warren Buffett - Lighter Gray
            "8944783c-2e2b-4796-b92e-21dacca776bc": new THREE.Color(0xa5a5a5), // Da Vinci - Gray
            "3542f19e-4e07-4828-8f9d-f330e4d6d660": new THREE.Color(0xa8a8a8), // Plato - Gray
            //"897eb00c-4f16-4f73-81d5-d1c91fad53f2": new THREE.Color(0xa8a8a8), //Charles Darwin - Gray
            "4d7d2e17-b818-4144-b78b-629c688a71ac": new THREE.Color(0x807c7b), //Karl Marx - Gray
            //"992e9007-b4f8-4711-80c2-884daf3d9960": new THREE.Color(0x6b4b30), // Van Gogh - Darker Red/brown
            //"278dddd6-b4a8-4303-a41d-be6219dbc907": new THREE.Color(0xa8a4a3), // Albert Einstein - Gray
            // "5a16ff56-6e03-414e-a89d-cda9d83fae60": new THREE.Color(0x4f4f4f), // Henry Ford - Darker Gray
            //"b20dc26f-8924-4aee-9ef5-9b98c0367a34": new THREE.Color(0xb0b0b0), // Ernest Hemingway - Darker Gray
        };

        // Eyebrow color mapping for different character models by model ID
        this.modelEyebrowColors = {
            // Default eyebrow color (slightly darker than hair for more definition)
            default: new THREE.Color(0x0a0a0a),

            //"278dddd6-b4a8-4303-a41d-be6219dbc907": new THREE.Color(0x4b3523), // Albert Einstein - Gray
            //"897eb00c-4f16-4f73-81d5-d1c91fad53f2": new THREE.Color(0x4b3523), //Charles Darwin - Gray
        };

        this.modelMoustacheColors = {
            // Default Moustache color (slightly darker than hair for more definition)
            default: new THREE.Color(0x0000),
            //"44f87c0b-ca22-4225-9fea-8d823e6a0160": new THREE.Color(0xb0b0b0), // Warren Buffett - Gray
            "8944783c-2e2b-4796-b92e-21dacca776bc": new THREE.Color(0xb0b0b0), // Da Vinci - Gray
            "3542f19e-4e07-4828-8f9d-f330e4d6d660": new THREE.Color(0xb0b0b0), //Plato - Gray
            // "897eb00c-4f16-4f73-81d5-d1c91fad53f2": new THREE.Color(0xb0b0b0), //Charles Darwin - Gray
            "4d7d2e17-b818-4144-b78b-629c688a71ac": new THREE.Color(0x00000), // Karl Marx
            //"992e9007-b4f8-4711-80c2-884daf3d9960": new THREE.Color(0x6b4b30), // Van Gogh - Darker Red/brown
            //"278dddd6-b4a8-4303-a41d-be6219dbc907": new THREE.Color(0xa8a4a3), // Albert Einstein - Gray
            //"5a16ff56-6e03-414e-a89d-cda9d83fae60": new THREE.Color(0x4f4f4f), // Henry Ford - Darker Gray
            // "b20dc26f-8924-4aee-9ef5-9b98c0367a34": new THREE.Color(0xb0b0b0), // Ernest Hemingway - Gray
        };

        // Model IDs where glasses should be treated as hair elements (use hair material values)
        this.glassesAsHairModels = new Set([
            // Add model IDs here for characters whose glasses should use hair material
            //"b20dc26f-8924-4aee-9ef5-9b98c0367a34", // Ernest Hemingway
        ]);

        // Current model ID (will be updated when model changes)
    }

    setupTransparencyControls() {
        this.defaultValues = {
            fronthair: 0.85,
            sidehair: 0.85,
            moustache: 0.9,
            beard: 0.9,
            HAIR1_3: 0.85,
            Haircards: 0.85,
            hairlowpoly: 0.85,
            hair: 0.85,
            eyebrows: 0.9,
            eyelashes: 0.85,
        };

        Object.entries(this.defaultValues).forEach(([type, value]) => {
            this.updateHairTransparency(type, value);
        });
    }

    _getSafeMaterial(mesh) {
        if (!mesh.material) return null;
        if (Array.isArray(mesh.material)) {
            return mesh.material[0] || null;
        }
        return mesh.material;
    }

    async setupMaterialForMesh(mesh, scene) {
        if (!mesh || !mesh.isMesh) return null;

        try {
            const meshName = mesh.name || "";
            const safeMaterial = this._getSafeMaterial(mesh);
            if (!safeMaterial) {
                console.warn("[PBR] Mesh has no material, skipping:", meshName);
                return null;
            }

            if (Array.isArray(mesh.material)) {
                console.log(
                    "[PBR] Multi-material mesh detected, using first material:",
                    meshName,
                );
                mesh.material = safeMaterial;
            }

            const isGlasses = meshName.includes("glasses");
            const shouldTreatGlassesAsHair =
                isGlasses && this.glassesAsHairModels.has(this.currentModelId);
            const isHairElement =
                meshName.includes("HAIR1_3") ||
                meshName.includes("Haircards") ||
                meshName.includes("hairlowpoly") ||
                meshName.includes("hair") ||
                shouldTreatGlassesAsHair ||
                meshName.toLowerCase().includes("moustache") ||
                meshName.toLowerCase().includes("beard") ||
                meshName.toLowerCase().includes("eyebrows") ||
                meshName.toLowerCase().includes("eyelashes");

            if (isHairElement) {
                this.hairElements.set(mesh.uuid, mesh);
                const oldMaterial = safeMaterial;

                if (meshName.toLowerCase().includes("eyelashes")) {
                    console.log("Eyelash mesh detected:", meshName);

                    // Load and verify texture
                    const textureLoader = new THREE.TextureLoader();
                    const eyelashTexture = textureLoader.load(
                        "/static/textures/eyelashes.png",
                        () => {
                            console.log("Eyelash texture loaded successfully!");
                        },
                    );

                    eyelashTexture.colorSpace = THREE.SRGBColorSpace;
                    const eyelashesColor = this.getHairColorForCurrentModel();

                    // Enhanced material
                    const eyelashMaterial = new THREE.MeshStandardMaterial({
                        map: eyelashTexture,
                        alphaMap: eyelashTexture,
                        color: eyelashesColor,
                        transparent: true,
                        opacity: 1.0,
                        alphaTest: 0, // Lower threshold for better details
                        side: THREE.DoubleSide,
                        roughness: 0.2,
                        metalness: 0.3,
                        envMapIntensity: 1.2,
                        depthWrite: false,
                        depthTest: true,
                    });

                    // Apply material
                    mesh.material = eyelashMaterial;
                    mesh.renderOrder = 3000; // Ensure rendering above the face
                    mesh.castShadow = false;
                    mesh.receiveShadow = false;
                    this.materials.set(mesh.uuid, eyelashMaterial);
                    this.duplicateHairCards(mesh, scene);
                    return eyelashMaterial;
                } else if (meshName.toLowerCase().includes("eyebrows")) {
                    const eyebrowColor = this.getEyebrowColorForCurrentModel();
                    const safeMap =
                        oldMaterial.map && oldMaterial.map.isTexture
                            ? oldMaterial.map
                            : null;
                    const safeAlphaMap =
                        oldMaterial.alphaMap && oldMaterial.alphaMap.isTexture
                            ? oldMaterial.alphaMap
                            : safeMap;
                    const alphaTestValue = 0.15;
                    const opacity = 0.9;
                    const newMaterial = new THREE.MeshStandardMaterial({
                        map: safeMap,
                        alphaMap: safeAlphaMap,
                        color: eyebrowColor,
                        transparent: true,
                        opacity: opacity,
                        alphaTest: alphaTestValue,
                        side: THREE.DoubleSide,
                        roughness: 0.9,
                        metalness: 0.1,
                        envMapIntensity: 0.3,
                        depthWrite: false,
                    });
                    mesh.renderOrder = 1100;
                    mesh.material = newMaterial;
                    this.materials.set(mesh.uuid, newMaterial);
                    this.duplicateHairCards(mesh, scene);
                    return newMaterial;
                } else if (meshName.toLowerCase().includes("beard")) {
                    const beardColor = this.getBeardColorForCurrentModel();
                    const safeMap =
                        oldMaterial.map && oldMaterial.map.isTexture
                            ? oldMaterial.map
                            : null;
                    const safeAlphaMap =
                        oldMaterial.alphaMap && oldMaterial.alphaMap.isTexture
                            ? oldMaterial.alphaMap
                            : safeMap;
                    const alphaTestValue = 0.15;
                    const opacity = 0.9;
                    const newMaterial = new THREE.MeshStandardMaterial({
                        map: safeMap,
                        alphaMap: safeAlphaMap,
                        color: beardColor,
                        transparent: true,
                        opacity: opacity,
                        alphaTest: alphaTestValue,
                        side: THREE.DoubleSide,
                        roughness: 0.9,
                        metalness: 0.1,
                        envMapIntensity: 0.3,
                        depthWrite: false,
                    });
                    mesh.renderOrder = 1100;
                    mesh.material = newMaterial;
                    this.materials.set(mesh.uuid, newMaterial);
                    //this.duplicateHairCards(mesh, scene);
                    return newMaterial;
                } else if (meshName.toLowerCase().includes("moustache")) {
                    const moustacheColor =
                        this.getMoustacheColorForCurrentModel();
                    const safeMap =
                        oldMaterial.map && oldMaterial.map.isTexture
                            ? oldMaterial.map
                            : null;
                    const safeAlphaMap =
                        oldMaterial.alphaMap && oldMaterial.alphaMap.isTexture
                            ? oldMaterial.alphaMap
                            : safeMap;
                    const alphaTestValue = 0.15;
                    const opacity = 0.9;
                    const newMaterial = new THREE.MeshStandardMaterial({
                        map: safeMap,
                        alphaMap: safeAlphaMap,
                        color: moustacheColor,
                        transparent: true,
                        opacity: opacity,
                        alphaTest: alphaTestValue,
                        side: THREE.DoubleSide,
                        roughness: 0.9,
                        metalness: 0.1,
                        envMapIntensity: 0.3,
                        depthWrite: false,
                    });
                    mesh.renderOrder = 1100;
                    mesh.material = newMaterial;
                    this.materials.set(mesh.uuid, newMaterial);
                    //this.duplicateHairCards(mesh, scene);
                    return newMaterial;
                } else {
                    const hairColor = this.getHairColorForCurrentModel();
                    const safeMap =
                        oldMaterial.map && oldMaterial.map.isTexture
                            ? oldMaterial.map
                            : null;
                    const safeAlphaMap =
                        oldMaterial.alphaMap && oldMaterial.alphaMap.isTexture
                            ? oldMaterial.alphaMap
                            : safeMap;
                    const alphaTestValue = 0.2;
                    const opacity = 0.85;
                    const newMaterial = new THREE.MeshStandardMaterial({
                        map: safeMap,
                        alphaMap: safeAlphaMap,
                        color: hairColor,
                        transparent: true,
                        opacity: opacity,
                        alphaTest: alphaTestValue,
                        side: THREE.DoubleSide,
                        roughness: 0.7,
                        metalness: 0.3,
                        envMapIntensity: 1.0,
                        depthWrite: false,
                    });
                    mesh.renderOrder = 900;
                    mesh.material = newMaterial;
                    this.materials.set(mesh.uuid, newMaterial);
                    this.duplicateHairCards(mesh, scene);
                    return newMaterial;
                }
            }

            // Check if this mesh is part of the character (face, body, skin, etc.)
            const isSkinMesh =
                meshName.toLowerCase().includes("head") ||
                meshName.toLowerCase().includes("face") ||
                meshName.toLowerCase().includes("body") ||
                meshName.toLowerCase().includes("skin");

            if (!(safeMaterial instanceof THREE.MeshStandardMaterial)) {
                const oldMaterial = safeMaterial;
                const oldMap =
                    oldMaterial.map && typeof oldMaterial.map !== "function"
                        ? oldMaterial.map
                        : null;
                const oldNormalMap =
                    oldMaterial.normalMap &&
                    typeof oldMaterial.normalMap !== "function"
                        ? oldMaterial.normalMap
                        : null;
                const oldAlphaMap =
                    oldMaterial.alphaMap &&
                    typeof oldMaterial.alphaMap !== "function"
                        ? oldMaterial.alphaMap
                        : null;
                const oldColor =
                    oldMaterial.color instanceof THREE.Color
                        ? oldMaterial.color
                        : new THREE.Color(0xcccccc);
                const enhancedColor =
                    isSkinMesh && oldColor
                        ? new THREE.Color(oldColor).multiplyScalar(1.1)
                        : oldColor;

                const newMaterial = new THREE.MeshStandardMaterial({
                    map: oldMap,
                    normalMap: oldNormalMap,
                    color: enhancedColor,
                    transparent: oldMaterial.transparent || false,
                    opacity:
                        oldMaterial.opacity != null ? oldMaterial.opacity : 1.0,
                    side:
                        oldMaterial.side != null
                            ? oldMaterial.side
                            : THREE.FrontSide,
                    roughness: isSkinMesh ? 0.75 : 0.55, // Slightly reduced for more vibrancy
                    metalness: isSkinMesh ? 0.03 : 0.18, // Slightly increased for more reflectivity
                    envMapIntensity: isSkinMesh ? 0.6 : 1.0, // Increased environment reflections for vibrancy
                    normalScale: new THREE.Vector2(0.8, 0.8), // Subtle normal map intensity
                });
                mesh.material = newMaterial;
                this.materials.set(mesh.uuid, newMaterial);

                if (isSkinMesh) {
                    console.log(
                        "Applied low-reflection material to character skin mesh:",
                        meshName,
                    );
                }
            } else if (
                safeMaterial instanceof THREE.MeshStandardMaterial &&
                isSkinMesh
            ) {
                // If it's already a MeshStandardMaterial and is a skin mesh, adjust its properties
                // Enhanced skin material properties for vibrant realism
                mesh.material.roughness = 0.75; // Slightly reduced for more vibrancy
                mesh.material.metalness = 0.03; // Slightly increased for more reflectivity
                mesh.material.envMapIntensity = 0.6; // Increased environment reflections

                // Enhance color vibrancy for existing skin materials
                if (mesh.material.color) {
                    mesh.material.color.multiplyScalar(1.1);
                }

                mesh.material.needsUpdate = true;
                console.log(
                    "Adjusted existing material for character skin mesh:",
                    meshName,
                );
            }

            return mesh.material;
        } catch (error) {
            console.error("Error setting up material:", error);
            // Enhanced fallback material with better default properties
            const fallback = new THREE.MeshStandardMaterial({
                color: 0xcccccc,
                alphaTest: 0.3,
                side: THREE.DoubleSide,
                roughness: 0.6,
                metalness: 0.3,
                envMapIntensity: 1.2,
                normalScale: new THREE.Vector2(1.0, 1.0),
            });
            mesh.material = fallback;
            this.materials.set(mesh.uuid, fallback);
            return fallback;
        }
    }

    updateHairTransparency(hairType, value) {
        if (this._updateTimeout) {
            clearTimeout(this._updateTimeout);
        }

        this._updateTimeout = setTimeout(() => {
            const opacity = Math.max(0.65, value);
            const alphaTest = 0.05 + (1 - value) * 0.15;

            const updates = [];
            this.hairElements.forEach((mesh, uuid) => {
                const meshName = mesh.name;
                if (
                    meshName.includes(hairType) ||
                    (hairType === "HAIR1_3" && meshName.includes("HAIR1_3")) ||
                    (hairType === "Haircards" && meshName.includes("Haircards"))
                ) {
                    const material = this.materials.get(uuid);
                    if (material) {
                        updates.push(() => {
                            material.opacity = opacity;
                            material.alphaTest = alphaTest;
                            material.needsUpdate = true;
                        });

                        const duplicates = this.hairDuplicates.get(uuid);
                        if (duplicates) {
                            duplicates.forEach((duplicate, index) => {
                                const dupeMaterial = duplicate.material;
                                updates.push(() => {
                                    dupeMaterial.opacity = Math.max(
                                        0.6,
                                        opacity * (1 - index * 0.1),
                                    );
                                    dupeMaterial.alphaTest = alphaTest;
                                    dupeMaterial.needsUpdate = true;
                                });
                            });
                        }
                    }
                }
            });

            if (updates.length > 0) {
                requestAnimationFrame(() => {
                    updates.forEach((update) => update());
                });
            }
        }, 16);
    }

    duplicateHairCards(mesh, scene) {
        if (!mesh || !scene) return;

        const hairTypes = [
            "fronthair",
            "sidehair",
            "HAIR1_3",
            "Haircards",
            "hairlowpoly",
            "hair",
            "eyelashes",
        ];
        const meshName = mesh.name || "";
        const isHairMesh = hairTypes.some((type) => meshName.includes(type));

        if (!isHairMesh) return;

        if (
            !mesh.material ||
            Array.isArray(mesh.material) ||
            typeof mesh.material.clone !== "function"
        ) {
            console.warn(
                "[PBR] Cannot duplicate hair cards - invalid material on mesh:",
                meshName,
            );
            return;
        }

        if (!mesh.geometry) {
            console.warn(
                "[PBR] Cannot duplicate hair cards - no geometry on mesh:",
                meshName,
            );
            return;
        }

        const duplicates = [];
        const numDuplicates = 3;
        const offsetDistance = 0.005;

        try {
            for (let i = 0; i < numDuplicates; i++) {
                const duplicate = mesh.clone();
                duplicate.material = mesh.material.clone();

                const normalOffset = new THREE.Vector3(
                    (Math.random() - 0.5) * 0.002,
                    (Math.random() - 0.5) * 0.002,
                    1,
                );
                normalOffset.normalize();
                normalOffset.multiplyScalar(offsetDistance * (i + 1));
                duplicate.position.add(normalOffset);

                duplicate.material.opacity = Math.max(
                    0.6,
                    (mesh.material.opacity || 1.0) * (1 - i * 0.1),
                );
                duplicate.material.alphaTest = 0.1;
                duplicate.renderOrder = 2000 + i;

                scene.add(duplicate);
                duplicates.push(duplicate);
            }
        } catch (error) {
            console.warn(
                "[PBR] Error duplicating hair cards for mesh:",
                meshName,
                error,
            );
            return;
        }

        this.hairDuplicates.set(mesh.uuid, duplicates);
    }

    async setupPlatformMaterial(mesh, textureConfig) {
        if (!mesh || !mesh.isMesh) {
            console.warn("Invalid mesh provided to setupPlatformMaterial");
            return null;
        }

        try {
            console.log(
                "Setting up platform with high-quality PBR material, config:",
                textureConfig,
            );

            // Create a higher quality PBR material for the platform
            const material = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                side: THREE.DoubleSide,
                roughness: 0.2, // Decreased from 0.5 to 0.2 for smoother surface
                metalness: 0.15, // Lowered metalness for less reflectivity
                envMapIntensity: 1.2,
                flatShading: false,
                shadowSide: THREE.FrontSide,
                // Note: anisotropy is a texture property, not a material property
            });

            // Apply material to mesh immediately (don't wait for texture)
            mesh.material = material;
            this.materials.set(mesh.uuid, material);

            // Enable shadows for the platform mesh for better visual quality
            mesh.receiveShadow = true;
            mesh.castShadow = false;

            // Create a TextureLoader with better quality settings
            const textureLoader = new THREE.TextureLoader();
            textureLoader.setCrossOrigin(""); // Allow loading from same domain
            console.log("Setting up TextureLoader for platform textures");
            const loadingPromises = [];

            // Load all available textures in parallel
            if (textureConfig) {
                // Load albedo/diffuse texture (base color)
                if (textureConfig.albedo) {
                    const albedoPromise = new Promise((resolve) => {
                        textureLoader.load(
                            textureConfig.albedo,
                            (texture) => {
                                console.log(
                                    "Successfully loaded albedo texture for platform",
                                );
                                // Apply high-quality texture settings
                                texture.wrapS = texture.wrapT =
                                    THREE.RepeatWrapping;
                                texture.repeat.set(1, 1);
                                // Apply gamma correction for better color rendering
                                texture.colorSpace = THREE.SRGBColorSpace;
                                // Enable anisotropic filtering for better quality at angles
                                texture.anisotropy = 16;
                                // Apply mipmapping for better quality at different distances
                                texture.minFilter =
                                    THREE.LinearMipmapLinearFilter;
                                texture.magFilter = THREE.LinearFilter;
                                texture.generateMipmaps = true;

                                // Apply texture to material
                                material.map = texture;
                                material.needsUpdate = true;
                                resolve();
                            },
                            undefined,
                            (error) => {
                                console.error(
                                    `Error loading platform albedo texture: ${error.message}`,
                                );
                                resolve();
                            },
                        );
                    });
                    loadingPromises.push(albedoPromise);
                }

                // Load normal map for surface detail
                if (textureConfig.normal) {
                    const normalPromise = new Promise((resolve) => {
                        textureLoader.load(
                            textureConfig.normal,
                            (texture) => {
                                console.log(
                                    "Successfully loaded normal map for platform",
                                );
                                texture.wrapS = texture.wrapT =
                                    THREE.RepeatWrapping;
                                texture.repeat.set(1, 1);
                                // Non-color data doesn't need gamma correction
                                // Enable anisotropic filtering
                                texture.anisotropy = 16;
                                // Apply mipmapping
                                texture.minFilter =
                                    THREE.LinearMipmapLinearFilter;
                                texture.magFilter = THREE.LinearFilter;
                                texture.generateMipmaps = true;

                                // Apply normal map with reduced intensity
                                material.normalMap = texture;
                                material.normalScale = new THREE.Vector2(
                                    0.3,
                                    0.3,
                                ); // Reduced from 1.5 to 0.3
                                material.needsUpdate = true;
                                resolve();
                            },
                            undefined,
                            (error) => {
                                console.error(
                                    `Error loading platform normal map: ${error.message}`,
                                );
                                resolve();
                            },
                        );
                    });
                    loadingPromises.push(normalPromise);
                }

                // Load roughness map for realistic surface properties
                if (textureConfig.roughness) {
                    const roughnessPromise = new Promise((resolve) => {
                        textureLoader.load(
                            textureConfig.roughness,
                            (texture) => {
                                console.log(
                                    "Successfully loaded roughness map for platform",
                                );
                                texture.wrapS = texture.wrapT =
                                    THREE.RepeatWrapping;
                                texture.repeat.set(1, 1);
                                // Enable anisotropic filtering
                                texture.anisotropy = 16;
                                // Apply mipmapping
                                texture.minFilter =
                                    THREE.LinearMipmapLinearFilter;
                                texture.magFilter = THREE.LinearFilter;
                                texture.generateMipmaps = true;

                                // Apply roughness map
                                material.roughnessMap = texture;
                                material.needsUpdate = true;
                                resolve();
                            },
                            undefined,
                            (error) => {
                                console.error(
                                    `Error loading platform roughness map: ${error.message}`,
                                );
                                resolve();
                            },
                        );
                    });
                    loadingPromises.push(roughnessPromise);
                }

                // Load ambient occlusion map for depth and shadow details
                if (textureConfig.ao) {
                    const aoPromise = new Promise((resolve) => {
                        textureLoader.load(
                            textureConfig.ao,
                            (texture) => {
                                console.log(
                                    "Successfully loaded AO map for platform",
                                );
                                texture.wrapS = texture.wrapT =
                                    THREE.RepeatWrapping;
                                texture.repeat.set(1, 1);
                                // Enable anisotropic filtering
                                texture.anisotropy = 16;
                                // Apply mipmapping
                                texture.minFilter =
                                    THREE.LinearMipmapLinearFilter;
                                texture.magFilter = THREE.LinearFilter;
                                texture.generateMipmaps = true;

                                // Apply ambient occlusion map with increased intensity
                                material.aoMap = texture;
                                material.aoMapIntensity = 1.2; // Increase AO intensity
                                material.needsUpdate = true;
                                resolve();
                            },
                            undefined,
                            (error) => {
                                console.error(
                                    `Error loading platform AO map: ${error.message}`,
                                );
                                resolve();
                            },
                        );
                    });
                    loadingPromises.push(aoPromise);
                }
            }

            // Wait for all textures to load
            Promise.all(loadingPromises).then(() => {
                console.log("All platform textures loaded successfully");
                material.needsUpdate = true;
            });

            return material;
        } catch (error) {
            console.error("Error setting up platform material:", error);
            // Simplified fallback material - pure white with no lighting effects
            const fallback = new THREE.MeshBasicMaterial({
                color: 0xffffff, // Pure white
                side: THREE.DoubleSide,
            });

            if (mesh) {
                mesh.material = fallback;
                mesh.receiveShadow = false;
                mesh.castShadow = false;
                this.materials.set(mesh.uuid, fallback);
            }

            return fallback;
        }
    }

    async loadCustomTextures(textureConfig) {
        const textures = {};
        console.log("Loading textures with config:", textureConfig);

        for (const [key, path] of Object.entries(textureConfig)) {
            if (path) {
                try {
                    console.log(`Loading ${key} texture from ${path}`);
                    textures[key] = await this.textureLoader.loadAsync(path);
                    textures[key].wrapS = textures[key].wrapT =
                        THREE.RepeatWrapping;
                    textures[key].repeat.set(1, 1);
                    console.log(`Successfully loaded ${key} texture`);
                } catch (error) {
                    console.error(
                        `Error loading texture ${key} from path ${path}:`,
                        error,
                    );
                    // Continue without this texture rather than failing completely
                    textures[key] = null;
                }
            }
        }

        return textures;
    }

    /**
     * Sets the current model ID to ensure correct hair and beard coloring
     * @param {string} modelId - The ID of the currently loaded model
     */
    setCurrentModelId(modelId) {
        console.log("PBRMaterialManager: Setting model ID to", modelId);
        this.currentModelId = modelId;

        // Update materials for existing hair elements with the new colors
        const hairColor = this.getHairColorForCurrentModel();
        const beardColor = this.getBeardColorForCurrentModel();
        const eyebrowColor = this.getEyebrowColorForCurrentModel();
        const moustacheColor = this.getMoustacheColorForCurrentModel();

        this.hairElements.forEach((mesh, uuid) => {
            const material = this.materials.get(uuid);
            if (material && material.color) {
                // Apply different colors based on element type
                if (mesh.name.toLowerCase().includes("beard")) {
                    material.color.copy(beardColor);
                } else if (mesh.name.toLowerCase().includes("eyebrows")) {
                    material.color.copy(eyebrowColor);
                } else if (mesh.name.toLowerCase().includes("moustache")) {
                    material.color.copy(moustacheColor);
                } else {
                    material.color.copy(hairColor);
                }
                material.needsUpdate = true;

                // Update any duplicates as well
                const duplicates = this.hairDuplicates.get(uuid);
                if (duplicates) {
                    duplicates.forEach((duplicate) => {
                        if (duplicate.material && duplicate.material.color) {
                            if (mesh.name.toLowerCase().includes("beard")) {
                                duplicate.material.color.copy(beardColor);
                            } else if (
                                mesh.name.toLowerCase().includes("eyebrows")
                            ) {
                                duplicate.material.color.copy(eyebrowColor);
                            } else if (
                                mesh.name.toLowerCase().includes("moustache")
                            ) {
                                duplicate.material.color.copy(moustacheColor);
                            } else {
                                duplicate.material.color.copy(hairColor);
                            }
                            duplicate.material.needsUpdate = true;
                        }
                    });
                }
            }
        });
    }

    /**
     * Get the appropriate hair color for the current model
     * @returns {THREE.Color} - The hair color for the current model
     */
    getHairColorForCurrentModel() {
        // If we have a specific color for this model, use it
        if (this.currentModelId && this.modelHairColors[this.currentModelId]) {
            return this.modelHairColors[this.currentModelId];
        }

        // Otherwise use the default color
        return this.modelHairColors["default"];
    }

    /**
     * Get the appropriate beard color for the current model
     * @returns {THREE.Color} - The beard color for the current model
     */
    getBeardColorForCurrentModel() {
        // If we have a specific beard color for this model, use it
        if (this.currentModelId && this.modelBeardColors[this.currentModelId]) {
            return this.modelBeardColors[this.currentModelId];
        }

        // Otherwise use the default beard color
        return this.modelBeardColors["default"];
    }

    getMoustacheColorForCurrentModel() {
        // If we have a specific beard color for this model, use it
        if (
            this.currentModelId &&
            this.modelMoustacheColors[this.currentModelId]
        ) {
            return this.modelMoustacheColors[this.currentModelId];
        }

        // Otherwise use the default beard color
        return this.modelMoustacheColors["default"];
    }

    /**
     * Get the appropriate eyebrow color for the current model
     * @returns {THREE.Color} - The eyebrow color for the current model
     */
    getEyebrowColorForCurrentModel() {
        // If we have a specific eyebrow color for this model, use it
        if (
            this.currentModelId &&
            this.modelEyebrowColors[this.currentModelId]
        ) {
            return this.modelEyebrowColors[this.currentModelId];
        }

        // Otherwise use the default eyebrow color
        return this.modelEyebrowColors["default"];
    }
}
